import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@infinitform/editor-features';

/**
 * Object storage signs its own URLs, so our token buys nothing there and costs
 * plenty: Authorization is not a safelisted header, so it turns a plain GET
 * into a preflight the distribution answers with 405, and withCredentials
 * makes the browser reject a response that does not allow credentials.
 * Results now come from the CDN as well as from S3 directly.
 */
function isExternalStorageUrl(url: string): boolean {
  return url.includes('.amazonaws.com') || url.includes('.cloudfront.net');
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private router = inject(Router);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth for auth endpoints but include credentials for cookies
    if (request.url.includes('/auth/')) {
      return next.handle(request.clone({ withCredentials: true }));
    }

    if (isExternalStorageUrl(request.url)) {
      return next.handle(request);
    }

    // Get access token (includes URL-based tokens for shared projects)
    const accessToken = this.authService.getAccessToken();

    if (accessToken) {
      request = request.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
        withCredentials: true // Include cookies for refresh token
      });
    } else {
      // Include credentials even without token for potential cookie-based auth
      request = request.clone({ withCredentials: true });
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Try to refresh token using cookie-based refresh
          return from(firstValueFrom(this.authService.refreshToken())).pipe(
            switchMap((response: any) => {
              // Retry original request with new token
              const newRequest = request.clone({
                setHeaders: { Authorization: `Bearer ${response.access_token}` },
                withCredentials: true
              });
              return next.handle(newRequest);
            }),
            catchError(() => {
              // Refresh failed, redirect to unauthorized screen
              this.router.navigate(['/flow-status'], {
                state: {
                  type: 'unauthorized'
                }
              });
              return throwError(() => error);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }
}
